import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Collapse, Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import ViewOption from "../components/ViewOption";
import FilterWrapper from "../components/general/FiltersWrapper";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableTextField from "../components/general/ReusableTextField";
import HeavyTable from "../components/table/HeavyTable";
import {
  buildQueryString,
  changeViewOptions,
  controlFilter,
  controlView,
  makeSureIsArray,
} from "../functions/general";
import { getChampions } from "../functions/getData";
import { generateChampions } from "../functions/postData";
import { showNotification } from "../functions/snackbar";

const championsHeader = [
  { id: "student_name", label: "Student Name" },
  { id: "index", label: "Index" },
  { id: "grade", label: "Grade" },
  { id: "rank", label: "Rank" },
  { id: "final_score", label: "Final Score" },
  { id: "points", label: "Points" },
  { id: "country", label: "Country" },
  { id: "organization", label: "Organization" },
  { id: "team_number", label: "Team Number" },
];

export default function ChampionsPage() {
  document.title = "Champions";
  const isMounted = useRef(false);
  const [header, setHeader] = useState(championsHeader);
  const [championsTable, setChampionsTable] = useState();
  const [viewOptions, setViewOptions] = useState([
    { label: "Student Name", state: true, key: "student_name" },
    { label: "Index", state: true, key: "index" },
    { label: "Grade", state: true, key: "grade" },
    { label: "Country", state: true, key: "country" },
    { label: "Organization", state: true, key: "organization" },
    { label: "Team Number", state: true, key: "team_number" },
    { label: "Points", state: true, key: "points" },
    { label: "Final Score", state: true, key: "final_score" },
    { label: "Rank", state: true, key: "rank" },
  ]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [sortAbleColumns, setSortableColumns] = useState([]);
  const [competitionId, setCompetitionId] = useState("");

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    grade: "",
    rank: "",
    orderBy: "",
    orderDirection: "",
  });

  const handleView = () => controlView(viewing, setViewing, setFiltering);
  const handleFilter = () => controlFilter(filtering, setFiltering, setViewing);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const isFirstRender = useRef(true);

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      grade: "",
      rank: "",
      orderBy: "",
      orderDirection: "",
    });
  };

  const adjustEndpoint = (endpoint, value) => {
    const currentParams = { ...queryParams };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.grade && currentParams.grade) {
      endpoint += `&grade=${currentParams.grade}`;
    }
    if (value !== currentParams.rank && currentParams.rank) {
      endpoint += `&rank=${currentParams.rank}`;
    }

    return endpoint;
  };

  const filters = [
    {
      label: "Grade",
      endpoint: `competition/team-based/simoc/${competitionId}/champions?get_filter=grade`,
      value: queryParams.grade,
      onChange: (value) => updateQueryParams({ grade: value }),
      key: "grade",
      setValue: (value) => updateQueryParams({ grade: value }),
    },
    {
      label: "Rank",
      endpoint: `competition/team-based/simoc/${competitionId}/champions?get_filter=rank`,
      value: queryParams.rank,
      onChange: (value) => updateQueryParams({ rank: value }),
      key: "rank",
      setValue: (value) => updateQueryParams({ rank: value }),
    },
  ];

  const onChangeViewOptions = (value) =>
    changeViewOptions(championsHeader, setViewOptions, setHeader, value);

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({
      search: value,
      orderBy: "rank",
      orderDirection: "asc",
    });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const formatPointsData = (pointsObj) => {
    if (!pointsObj || typeof pointsObj !== "object") return "-";

    return Object.entries(pointsObj)
      .map(([category, data]) => {
        const award = data?.award || "-";
        const points =
          data?.points !== null && data?.points !== undefined
            ? data.points
            : "-";
        return `${category}: ${award} (${points})`;
      })
      .join(" | ");
  };
  const formatPointsForTooltip = (pointsObj) => {
    if (!pointsObj || typeof pointsObj !== "object") return [];

    return Object.entries(pointsObj).map(([category, data]) => ({
      category: category,
      award: data?.award || "-",
      points:
        data?.points !== null && data?.points !== undefined ? data.points : "-",
    }));
  };

  const formatTableData = (data) => {
    console.log(data);
    return data.map((item) => ({
      ...item,
      student_name: item?.participant?.name || "-",
      index: item?.participant?.index_no || "-",
      grade: item?.participant?.participant_grade?.display_name || "-",
      country: item?.participant?.country?.display_name || "-",
      organization:
        item?.participant?.competition_organization?.organization?.name || "-",
      team_number: item?.team_number || "-",
      points: formatPointsData(item?.points),
      points_tooltip: formatPointsForTooltip(item?.points),
      final_score:
        item?.final_score !== null && item?.final_score !== undefined
          ? item.final_score
          : "-",
      rank: item?.rank !== null && item?.rank !== undefined ? item.rank : "-",
    }));
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const refetchChampionsData = () => {
    if (controller.current && competitionId) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      getChampions(competitionId, queryString, controller.current.signal)
        .then((res) => {
          setChampionsTable(convertToTableFormat(res));
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching champions:", error);
          setLoading(false);
        });
    }
  };

  const handleGenerateChampions = () => {
    if (!competitionId) {
      showNotification(
        "error",
        "No competition ID available",
        enqueueSnackbar,
        closeSnackbar
      );
      return;
    }

    setLoading(true);
    generateChampions(competitionId)
      .then((response) => {
        if ([200, 201].includes(response.status)) {
          showNotification(
            "success",
            "Champions generated successfully!",
            enqueueSnackbar,
            closeSnackbar
          );
          // Refetch the data after successful generation
          refetchChampionsData();
        } else {
          showNotification(
            "error",
            response.message || "Failed to generate champions",
            enqueueSnackbar,
            closeSnackbar
          );
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error generating champions:", error);
        showNotification(
          "error",
          "Failed to generate champions",
          enqueueSnackbar,
          closeSnackbar
        );
        setLoading(false);
      });
  };

  const controller = useRef(null);
  const location = useLocation();

  // Effect for handling URL changes and initial setup
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Handle competition_id from URL
    if (location.search.slice(0, 4) === "?id=") {
      const id = location.search.slice(4);
      setCompetitionId(id);
      updateQueryParams({});
    } else {
      // If no competition ID in URL, redirect or show error
      console.error("No competition ID provided in URL");
    }

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, [location.search]);

  const convertToTableFormat = (response) => {
    return {
      current_page: response.meta.current_page,
      data: formatTableData(response.data),
      first_page_url: response.meta.first_page_url,
      from: response.meta.from,
      last_page: response.meta.last_page,
      last_page_url: response.meta.last_page_url,
      links: response.meta.links,
      next_page_url: response.meta.next_page_url,
      path: response.meta.path,
      per_page: response.meta.per_page,
      prev_page_url: response.meta.prev_page_url,
      to: response.meta.to,
      total: response.meta.total,
    };
  };

  // Effect for data fetching
  useEffect(() => {
    if (controller.current && !isFirstRender.current && competitionId) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      getChampions(competitionId, queryString, controller.current.signal)
        .then((res) => {
          setChampionsTable(convertToTableFormat(res));
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching champions:", error);
          setLoading(false);
        });
    }
  }, [queryParams, competitionId]);
  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={viewing}>
        <ViewOption
          onChangeRowsPerPage={onChangeRowsPerPage}
          viewOptions={viewOptions}
          rowsPerPage={rowsPerPage}
          onChangeViewOptions={onChangeViewOptions}
        />
      </Collapse>
      <Collapse in={filtering}>
        <FilterWrapper filters={filters} adjustEndpoint={adjustEndpoint} />
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Champions"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="View Options"
            state={viewing}
            fontSize={14}
            height={42}
            onClick={handleView}
            marginRight={10}
          />
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={handleFilter}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container>
        <NunitoText
          value="Champions"
          fontSize={40}
          fontWeight={700}
          italic
          color="#144A94"
        />
        <Grid
          className="searchAndBtnContainer"
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <ReusableTextField
            type="search"
            width={500}
            height={60}
            bgColor="#F2F2F2"
            placeholder="Search champions"
            state={search}
            setState={onChangeSearch}
            onBlur
          />
          <ReusableButton
            text="Generate Champions"
            fontSize={15}
            bgColor="#5E75C3"
            height={46}
            marginRight={20}
            iconType="compute"
            onClick={handleGenerateChampions}
          />
        </Grid>
        <NunitoText
          value='Search in "Team Number", "Index"  and "Participant Name" columns'
          fontSize={20}
          fontWeight={400}
          italic
          color="#144A94"
        />
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          championsTable &&
          makeSureIsArray(championsTable?.data)?.length && (
            <HeavyTable
              sortableKeys={sortAbleColumns}
              headers={header}
              list={championsTable}
              title="champions"
              rowsPerPage={rowsPerPage}
              onChangePage={onChangePage}
              fixed={["student_name", "rank"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
            />
          )}
        {!loadingTable &&
          championsTable &&
          !makeSureIsArray(championsTable.data).length && (
            <NoData height={600} />
          )}
      </Grid>
    </Box>
  );
}
